import { useMemo } from 'react'

import { useSelector } from 'react-redux'

import { Flex, Text, Tooltip } from '@chakra-ui/react'

import { TableEditRemoveButton } from '@/features/sop-crud'
import { selectEditMode } from '@/redux/ui/uiSlice'

import { SOPsListItemFacilitiesData } from '../types/types'

const MAX_COLUMN_CHARS = 60 // Max characters shown in the column (used for width calculation)
const PILL_PADDING = 16 // Total left/right padding for each facility pill (8px left + 8px right)
const PILL_MARGIN = 4 // Space to the right of each pill
const MORE_PILL_CHARS = 8 // Characters needed for "+X more" pill
const PILL_CHAR_WIDTH = 7 // Average width (px) of each character in a pill
const TOOLTIP_OPEN_DELAY = 500 // Time (ms) before tooltip appears

const truncateFacilityName = (name: string, maxChars: number): string => {
  if (name.length <= maxChars) return name
  return name.substring(0, maxChars - 3) + '...'
}

export const FacilitiesCell = ({
  facilities,
  onRemove,
  onEdit,
}: {
  facilities: SOPsListItemFacilitiesData
  onRemove?: () => void
  onEdit?: () => void
}) => {
  const isEditMode = useSelector(selectEditMode)

  const facilitiesData = useMemo(() => {
    const activeFacilities = facilities?.filter((f) => !f?.node?.archived) || []
    const names = activeFacilities.map((f) => f?.node?.name).filter(Boolean)

    if (names.length === 0)
      return {
        visibleFacilities: [],
        hiddenFacilities: [],
        allNames: [],
      }

    let totalWidth = 0
    const visibleFacilities: string[] = []
    const hiddenFacilities: string[] = []

    // Reserve space for "+X more" if we might need it
    const reservedWidthForMorePill =
      names.length > 1 ? MORE_PILL_CHARS * PILL_CHAR_WIDTH : 0
    const maxWidthForFacilities =
      MAX_COLUMN_CHARS * PILL_CHAR_WIDTH - reservedWidthForMorePill

    for (const name of names) {
      const truncatedName = truncateFacilityName(name, MAX_COLUMN_CHARS)
      const pillWidth =
        truncatedName.length * PILL_CHAR_WIDTH + PILL_PADDING + PILL_MARGIN

      if (
        totalWidth + pillWidth <= maxWidthForFacilities ||
        visibleFacilities.length === 0
      ) {
        visibleFacilities.push(truncatedName)
        totalWidth += pillWidth
      } else {
        hiddenFacilities.push(name)
      }
    }

    return {
      visibleFacilities,
      hiddenFacilities,
      allNames: names,
    }
  }, [facilities])

  const { visibleFacilities, hiddenFacilities, allNames } = facilitiesData

  return (
    <Flex alignItems='center' justifyContent='space-between'>
      <Flex alignItems='center' flexWrap='nowrap' overflow='hidden'>
        {visibleFacilities.map((facilityName, index) => (
          <Tooltip
            hasArrow
            key={index}
            label={allNames[index]} // Show full name on hover
            openDelay={TOOLTIP_OPEN_DELAY}
            placement='auto'
          >
            <Text
              bg='rgba(9, 30, 66, 0.03)'
              borderRadius='100px'
              color='#172B4D'
              data-testid='sopsPage_table_facilityCell'
              flexShrink={0}
              fontSize='12px'
              fontWeight='normal'
              key={`facility-${index}`}
              mr='4px'
              px='8px'
              whiteSpace='nowrap'
            >
              {facilityName}
            </Text>
          </Tooltip>
        ))}

        {hiddenFacilities.length > 0 && (
          <Tooltip
            hasArrow
            label={
              <Flex direction='column' gap='2px'>
                {hiddenFacilities.map((facilityName, index) => (
                  <Text fontSize='12px' key={index}>
                    {facilityName}
                  </Text>
                ))}
              </Flex>
            }
            openDelay={TOOLTIP_OPEN_DELAY}
            placement='auto'
          >
            <Text
              bg='rgba(9, 30, 66, 0.08)'
              borderRadius='100px'
              color='#172B4D'
              data-testid='sopsPage_table_moreFacilitiesCell'
              flexShrink={0}
              fontSize='12px'
              fontWeight='normal'
              px='8px'
              whiteSpace='nowrap'
            >
              +{hiddenFacilities.length} more
            </Text>
          </Tooltip>
        )}
      </Flex>

      {isEditMode && (
        <TableEditRemoveButton
          handleOnEdit={onEdit}
          handleOnRemove={onRemove}
        />
      )}
    </Flex>
  )
}
